import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  MapPin, 
  DollarSign, 
  Clock, 
  Briefcase, 
  Users,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  Loader2,
  Heart,
  Share,
  Bell,
  TrendingUp,
  Building,
  BookOpen
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import LoadingSpinner from '../components/LoadingSpinner';

const JobItem = ({ job, index }) => {
  const formatSalary = (min, max) => {
    if (!min && !max) return 'Competitive salary';
    if (min && max) return `£${min?.toLocaleString()} - £${max?.toLocaleString()} per annum`;
    if (min) return `From £${min?.toLocaleString()} per annum`;
    return `Up to £${max?.toLocaleString()} per annum`;
  };

  const formatJobType = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Permanent';
  };

  const isNewJob = (date) => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return new Date(date) > oneWeekAgo;
  };

  // Generate a company name from job details for better presentation
  const getCompanyName = (job) => {
    return job.company || 'C9 Recruitment Client';
  };

  return (
    <div className="bg-white border border-gray-200 hover:border-c9-orange hover:shadow-lg rounded-xl p-5 transition-all duration-300 md:flex md:flex-col md:gap-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Link 
              to={`/jobs/${job.slug}`}
              className="text-xl font-bold text-c9-orange hover:text-c9-orange-alt transition-colors hover:underline"
            >
              {job.title}
            </Link>
            {isNewJob(job.created_at) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-500 text-white animate-pulse">
                NEW
              </span>
            )}
          </div>
          <div className="text-base font-semibold text-gray-700 mb-3">{getCompanyName(job)}</div>
        </div>
        <div className="flex items-center gap-2">
          <button className="text-gray-300 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50">
            <Heart className="h-5 w-5" />
          </button>
          <Link 
            to={`/jobs/${job.slug}`}
            className="bg-gradient-to-r from-c9-orange to-c9-orange-alt hover:from-c9-orange-alt hover:to-c9-orange text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2 transform hover:scale-105"
          >
            View Details
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Job Details Row */}
      <div className="flex flex-wrap items-center gap-6 mb-4 p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg">
        <div className="flex items-center gap-2 w-full md:w-1/2 lg:w-1/3 xl:w-1/4 sm:w-1/2">
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
            <DollarSign className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium">Salary</div>
            <div className="font-semibold text-gray-900 text-sm">{formatSalary(job.salary_min, job.salary_max)}</div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 w-full md:w-1/2 lg:w-1/3 xl:w-1/4 sm:w-1/2">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
            <MapPin className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium">Location</div>
            <div className="font-semibold text-gray-900 text-sm">{job.location}</div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 w-full md:w-1/2 lg:w-1/3 xl:w-1/4 sm:w-1/2">
          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
            <Clock className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-xs text-gray-500 font-medium">Posted</div>
            <div className="font-semibold text-gray-900 text-sm">
              {new Date(job.created_at).toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short'
              })}
            </div>
          </div>
        </div>
        
        <div className="ml-auto w-full md:w-1/2 lg:w-1/3 xl:w-1/4 sm:w-1/2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-c9-orange/10 text-c9-orange border border-c9-orange/20">
            {formatJobType(job.job_type)}
          </span>
        </div>
      </div>

      {/* Job Description Preview */}
      <p className="text-gray-700 text-sm leading-relaxed line-clamp-2 mb-4">
        {job.description?.replace(/<[^>]*>/g, '').substring(0, 200)}...
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <div className="flex items-center gap-3">
          <button className="text-xs text-gray-500 hover:text-c9-orange font-medium transition-colors px-2 py-1 rounded hover:bg-gray-100 flex items-center gap-1">
            <Share className="h-3 w-3" />
            Share
          </button>
          <span className="text-xs text-gray-500">ID: {job.id}</span>
        </div>
        <div className="text-xs text-gray-500">
          <span className="text-c9-orange hover:underline cursor-pointer">View similar jobs</span>
        </div>
      </div>
    </div>
  );
};

const JobFilters = ({ filters, onFilterChange, onClear }) => {
  const [openSections, setOpenSections] = useState({
    location: true,
    category: true,
    salary: true,
    jobType: true
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const categories = [
    'HGV Driver',
    'Van Driver',
    'Warehouse',
    'Admin',
    'Management',
    'Transport',
    'Logistics'
  ];

  const locations = [
    'London',
    'Birmingham',
    'Manchester',
    'Leeds',
    'Liverpool',
    'Bristol',
    'Sheffield',
    'Edinburgh',
    'Glasgow'
  ];

  const jobTypes = [
    'permanent',
    'contract',
    'temporary',
    'part-time'
  ];

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="bg-white border-r border-gray-100 pl-4 sm:pl-6 lg:pl-8 xl:pl-12 2xl:pl-16 pr-6 py-6 min-h-screen custom-scrollbar">
      <div className="sticky top-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-c9-orange/10 rounded-lg flex items-center justify-center">
              <Filter className="h-5 w-5 text-c9-orange" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Filter Jobs</h3>
            {activeFiltersCount > 0 && (
              <span className="bg-c9-orange text-white text-xs font-bold px-3 py-1 rounded-full">
                {activeFiltersCount} Active
              </span>
            )}
          </div>
          
          {activeFiltersCount > 0 && (
            <button
              onClick={onClear}
              className="text-sm text-c9-orange hover:text-c9-orange-alt font-medium transition-colors mb-6 flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Clear All Filters
            </button>
          )}
        </div>

        {/* Location Filter */}
        <div className="mb-6">
          <button
            onClick={() => toggleSection('location')}
            className="flex items-center justify-between w-full text-left py-3 border-b border-gray-300 hover:border-gray-400 transition-colors"
          >
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-c9-orange" />
              <span className="font-semibold text-gray-900">Location</span>
            </div>
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${openSections.location ? 'rotate-90' : ''}`} />
          </button>
          {openSections.location && (
            <div className="pt-4 pb-2">
              <select
                value={filters.location || ''}
                onChange={(e) => onFilterChange({ ...filters, location: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-c9-orange focus:border-c9-orange bg-white shadow-sm hover:border-gray-400 transition-colors"
              >
                <option value="">All locations</option>
                {locations.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
              {filters.location && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-c9-orange text-white">
                    {filters.location}
                    <button
                      onClick={() => onFilterChange({ ...filters, location: '' })}
                      className="ml-2 hover:bg-c9-orange-alt rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <button
            onClick={() => toggleSection('category')}
            className="flex items-center justify-between w-full text-left py-3 border-b border-gray-300 hover:border-gray-400 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Briefcase className="h-4 w-4 text-c9-orange" />
              <span className="font-semibold text-gray-900">Specialisation</span>
            </div>
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${openSections.category ? 'rotate-90' : ''}`} />
          </button>
          {openSections.category && (
            <div className="pt-4 pb-2">
              <select
                value={filters.category || ''}
                onChange={(e) => onFilterChange({ ...filters, category: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-c9-orange focus:border-c9-orange bg-white shadow-sm hover:border-gray-400 transition-colors"
              >
                <option value="">All categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              {filters.category && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-c9-orange text-white">
                    {filters.category}
                    <button
                      onClick={() => onFilterChange({ ...filters, category: '' })}
                      className="ml-2 hover:bg-c9-orange-alt rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Job Type Filter */}
        <div className="mb-6">
          <button
            onClick={() => toggleSection('jobType')}
            className="flex items-center justify-between w-full text-left py-3 border-b border-gray-300 hover:border-gray-400 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-c9-orange" />
              <span className="font-semibold text-gray-900">Contract Type</span>
            </div>
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${openSections.jobType ? 'rotate-90' : ''}`} />
          </button>
          {openSections.jobType && (
            <div className="pt-4 pb-2">
              <select
                value={filters.job_type || ''}
                onChange={(e) => onFilterChange({ ...filters, job_type: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-c9-orange focus:border-c9-orange bg-white shadow-sm hover:border-gray-400 transition-colors"
              >
                <option value="">All contract types</option>
                {jobTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
              {filters.job_type && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-c9-orange text-white">
                    {filters.job_type.charAt(0).toUpperCase() + filters.job_type.slice(1)}
                    <button
                      onClick={() => onFilterChange({ ...filters, job_type: '' })}
                      className="ml-2 hover:bg-c9-orange-alt rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Salary Filter */}
        <div className="mb-6">
          <button
            onClick={() => toggleSection('salary')}
            className="flex items-center justify-between w-full text-left py-3 border-b border-gray-300 hover:border-gray-400 transition-colors"
          >
            <div className="flex items-center gap-3">
              <DollarSign className="h-4 w-4 text-c9-orange" />
              <span className="font-semibold text-gray-900">Salary</span>
            </div>
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${openSections.salary ? 'rotate-90' : ''}`} />
          </button>
          {openSections.salary && (
            <div className="pt-4 pb-2 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Minimum</label>
                <input
                  type="number"
                  placeholder="£25,000"
                  value={filters.salary_min || ''}
                  onChange={(e) => onFilterChange({ ...filters, salary_min: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-c9-orange focus:border-c9-orange bg-white shadow-sm hover:border-gray-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Maximum</label>
                <input
                  type="number"
                  placeholder="£50,000"
                  value={filters.salary_max || ''}
                  onChange={(e) => onFilterChange({ ...filters, salary_max: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-c9-orange focus:border-c9-orange bg-white shadow-sm hover:border-gray-400 transition-colors"
                />
              </div>
              {(filters.salary_min || filters.salary_max) && (
                <div className="flex flex-wrap gap-2">
                  {filters.salary_min && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-c9-orange text-white">
                      Min: £{parseInt(filters.salary_min).toLocaleString()}
                      <button
                        onClick={() => onFilterChange({ ...filters, salary_min: '' })}
                        className="ml-2 hover:bg-c9-orange-alt rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                  {filters.salary_max && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-c9-orange text-white">
                      Max: £{parseInt(filters.salary_max).toLocaleString()}
                      <button
                        onClick={() => onFilterChange({ ...filters, salary_max: '' })}
                        className="ml-2 hover:bg-c9-orange-alt rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Filters */}
        <div className="border-t border-gray-300 pt-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Filters</h3>
          <div className="space-y-2">
            <button
              onClick={() => onFilterChange({ ...filters, salary_min: '30000' })}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-c9-orange rounded-lg transition-colors"
            >
              £30k+ Salary
            </button>
            <button
              onClick={() => onFilterChange({ ...filters, location: 'London' })}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-c9-orange rounded-lg transition-colors"
            >
              London Jobs
            </button>
            <button
              onClick={() => onFilterChange({ ...filters, job_type: 'permanent' })}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white hover:text-c9-orange rounded-lg transition-colors"
            >
              Permanent Roles
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [sortBy, setSortBy] = useState('newest');
  const [sortOrder, setSortOrder] = useState('desc');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    location: '',
    salary_min: '',
    salary_max: '',
    job_type: ''
  });
  const [resultsPerPage, setResultsPerPage] = useState(12);

  const isNewJob = (date) => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return new Date(date) > oneWeekAgo;
  };

  const fetchJobs = async (page = 1, searchQuery = searchTerm, currentFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: resultsPerPage.toString(),
        ...(searchQuery && { search: searchQuery }),
        ...currentFilters
      });

      const response = await fetch(`/.netlify/functions/get-jobs?${params}`);
      const data = await response.json();

      if (response.ok) {
        setJobs(data.jobs);
        setPagination(data.pagination);
      } else {
        console.error('Failed to fetch jobs:', data.error);
        setJobs([]);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(currentPage, searchTerm, filters);
  }, [currentPage, searchTerm, filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchJobs(1, searchTerm, filters);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({
      location: '',
      category: '',
      job_type: '',
      salary_min: '',
      salary_max: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    clearAllFilters();
  };

  const handleQuickApply = (job) => {
    // For now, redirect to job details page
    window.location.href = `/jobs/${job.slug}#apply`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f3f4f6;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #d1d5db #f3f4f6;
        }
      `}</style>
      
      <SEOHead 
        title="Current Job Vacancies - C9 Recruitment"
        description="Browse the latest transport and logistics job opportunities with C9 Recruitment. Find HGV driving, warehouse, and management positions across the UK."
        keywords="jobs, HGV driver jobs, transport jobs, logistics careers, UK transport recruitment"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-c9-blue via-c9-blue-alt to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-c9-blue/80 to-transparent"></div>
        
        {/* Floating shapes for visual interest */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-c9-orange/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-full blur-lg animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-c9-orange/5 rounded-full blur-lg"></div>
        
        <div className="relative container-full py-16 md:py-24">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-c9-orange/20 rounded-full text-c9-orange font-semibold text-sm mb-6 backdrop-blur-sm">
              Current Opportunities
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Find Your Next
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent"> Career Move</span>
            </h1>
            <p className="text-lg md:text-xl mb-12 max-w-4xl mx-auto text-gray-200 leading-relaxed">
              Discover exciting opportunities in transport and logistics with the UK's leading recruitment specialists
            </p>

            {/* Search Form in Hero */}
            <div className="max-w-3xl mx-auto mb-8">
              <form onSubmit={handleSearch}>
                <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-white/20 shadow-xl p-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by job title, company, or keywords..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-c9-orange/50 focus:border-c9-orange text-base bg-white hover:bg-gray-50 transition-all font-medium placeholder-gray-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-c9-orange to-c9-orange-alt hover:from-c9-orange-alt hover:to-c9-orange text-white px-6 py-3 rounded-lg font-semibold text-base transition-all shadow-lg hover:shadow-xl disabled:opacity-50 min-w-[120px]"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Search'}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-base">
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <Users className="h-5 w-5 mr-2 text-c9-orange" />
                <span className="font-semibold">500+ Active Jobs</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <Briefcase className="h-5 w-5 mr-2 text-c9-orange" />
                <span className="font-semibold">All Experience Levels</span>
              </div>
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <MapPin className="h-5 w-5 mr-2 text-c9-orange" />
                <span className="font-semibold">UK Wide Coverage</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="flex">
        {/* Left Sidebar - Filters */}
        <div 
          className="hidden lg:block w-1/4 bg-white border-r border-gray-100 sticky top-0 h-screen overflow-y-auto custom-scrollbar"
        >
          <JobFilters 
            filters={filters}
            onFilterChange={handleFilterChange}
            onClear={handleClearFilters}
          />
        </div>

        {/* Mobile Filter Toggle */}
        <div className="lg:hidden fixed bottom-4 right-4 z-50">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="bg-c9-orange hover:bg-c9-orange-alt text-white p-3 rounded-full shadow-lg transition-colors"
          >
            <Filter className="h-6 w-6" />
          </button>
        </div>

        {/* Mobile Filter Overlay */}
        {showMobileFilters && (
          <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowMobileFilters(false)}>
            <div className="bg-white w-full h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 custom-scrollbar" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Filters</h2>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <JobFilters 
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClear={handleClearFilters}
                />
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 px-4 lg:px-8 py-8">
          {/* Breadcrumbs */}
          <nav className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-c9-orange transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-gray-900 font-medium">Jobs</span>
            </div>
          </nav>

          {/* Search Results Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Search Results</h1>
              <p className="text-gray-600">
                You have <span className="font-semibold text-c9-orange">{pagination.total}</span> job results
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-4 sm:mt-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-c9-orange focus:border-c9-orange"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="salary-high">Salary: High to Low</option>
                  <option value="salary-low">Salary: Low to High</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <select 
                  value={resultsPerPage}
                  onChange={(e) => setResultsPerPage(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-c9-orange focus:border-c9-orange"
                >
                  <option value={12}>12 per page</option>
                  <option value={24}>24 per page</option>
                  <option value={48}>48 per page</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex gap-8">
            {/* Job Listings */}
            <div className="flex-1">
              {loading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <>
                  <div className="space-y-6">
                    {jobs.map((job) => (
                      <JobItem key={job.id} job={job} />
                    ))}
                    
                    {jobs.length === 0 && !loading && (
                      <div className="text-center py-12">
                        <div className="text-gray-500 mb-4">
                          <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          No jobs found matching your criteria
                        </div>
                        <p className="text-gray-600 mb-6">Try adjusting your search terms or filters</p>
                        <button
                          onClick={() => {
                            setSearchTerm('');
                            setFilters({
                              location: '',
                              category: '',
                              job_type: '',
                              salary_min: '',
                              salary_max: ''
                            });
                          }}
                          className="bg-c9-orange hover:bg-c9-orange-alt text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                        >
                          Clear All Filters
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 px-4 py-8 border-t border-gray-200">
                      {pagination.hasPrev && (
                        <button
                          onClick={() => setCurrentPage(currentPage - 1)}
                          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </button>
                      )}
                      
                      {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                        let page;
                        if (pagination.totalPages <= 5) {
                          page = i + 1;
                        } else if (currentPage <= 3) {
                          page = i + 1;
                        } else if (currentPage >= pagination.totalPages - 2) {
                          page = pagination.totalPages - 4 + i;
                        } else {
                          page = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                              page === currentPage
                                ? 'bg-c9-orange text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      {pagination.hasNext && (
                        <button
                          onClick={() => setCurrentPage(currentPage + 1)}
                          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="hidden xl:block w-1/4 bg-white border-l border-gray-100 px-6 py-8">
              <div className="sticky top-8 space-y-8">
                {/* Job Alerts Signup */}
                <div className="bg-gradient-to-br from-c9-orange/5 to-c9-orange/10 rounded-xl p-6 border border-c9-orange/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-c9-orange/20 rounded-lg flex items-center justify-center">
                      <Bell className="h-5 w-5 text-c9-orange" />
                    </div>
                    <h3 className="font-bold text-gray-900">Job Alerts</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Get notified when new jobs matching your criteria are posted.
                  </p>
                  <button className="w-full bg-c9-orange hover:bg-c9-orange-alt text-white px-4 py-2 rounded-lg font-medium transition-colors">
                    Set Up Alerts
                  </button>
                </div>

                {/* Popular Searches */}
                <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Search className="h-5 w-5 text-c9-orange" />
                    Popular Searches
                  </h3>
                  <div className="space-y-2">
                    {['HGV Driver', 'Warehouse', 'Management', 'Logistics', 'Forklift'].map((term) => (
                      <button
                        key={term}
                        onClick={() => setSearchTerm(term)}
                        className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-c9-orange hover:bg-c9-orange/5 rounded-lg transition-colors"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Career Resources */}
                <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-c9-orange" />
                    Career Resources
                  </h3>
                  <div className="space-y-3">
                    <a href="#" className="block p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="text-sm font-semibold text-gray-900">CV Writing Tips</div>
                      <div className="text-xs text-gray-600">Stand out from the crowd</div>
                    </a>
                    <a href="#" className="block p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="text-sm font-semibold text-gray-900">Interview Preparation</div>
                      <div className="text-xs text-gray-600">Ace your next interview</div>
                    </a>
                    <a href="#" className="block p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="text-sm font-semibold text-gray-900">Industry Insights</div>
                      <div className="text-xs text-gray-600">Latest transport trends</div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
